import {
  deleteDoc,
  writeBatch,
  type DocumentReference,
  type Firestore,
  type Query,
  type QuerySnapshot,
  type Transaction,
  type WriteBatch,
} from 'firebase/firestore';

/** Firestore caps write batches at 500 operations. */
export const FIRESTORE_BATCH_OP_LIMIT = 500;

/**
 * @deprecated The web Firestore client only supports `transaction.get(DocumentReference)`.
 * List refs with `getDocs(query(...))`, then `tx.get(ref)` for each document inside the transaction.
 */
export async function transactionGetQuery(
  tx: Transaction,
  q: Query,
): Promise<QuerySnapshot> {
  return (tx.get as unknown as (query: Query) => Promise<QuerySnapshot>)(q);
}

export async function commitBatchChunks<T>(
  db: Firestore,
  items: readonly T[],
  writeChunk: (batch: WriteBatch, chunk: readonly T[]) => void,
  maxChunkSize = FIRESTORE_BATCH_OP_LIMIT,
): Promise<void> {
  for (let i = 0; i < items.length; i += maxChunkSize) {
    const batch = writeBatch(db);
    writeChunk(batch, items.slice(i, i + maxChunkSize));
    await batch.commit();
  }
}

/** Delete many refs in chunked batches, optionally deleting a final ref on the last chunk. */
export async function deleteRefsInBatches(
  db: Firestore,
  refs: readonly DocumentReference[],
  finalRef?: DocumentReference,
  maxChunkSize = FIRESTORE_BATCH_OP_LIMIT - 1,
): Promise<void> {
  if (refs.length === 0) {
    if (finalRef) await deleteDoc(finalRef);
    return;
  }

  for (let i = 0; i < refs.length; i += maxChunkSize) {
    const batch = writeBatch(db);
    const chunk = refs.slice(i, i + maxChunkSize);
    chunk.forEach((ref) => batch.delete(ref));
    const isLastChunk = i + maxChunkSize >= refs.length;
    if (isLastChunk && finalRef) batch.delete(finalRef);
    await batch.commit();
  }
}
